import { Card, CardBody, EmptyState } from '@/shared/components';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiProblem } from '@/shared/api';
import { isWorkspaceAdmin, useAuth } from '@/shared/auth';
import { RevisionHoldRow } from './RevisionHoldRow';
import { MoreHolds, RevisionHoldsIntro } from './RevisionHoldsIntro';
import { RevisionHoldsUnavailable } from './RevisionHoldsUnavailable';
import { ReleaseNightsDialog } from './ReleaseNightsDialog';
import { withSettledRows } from './revisionHoldPresentation';
import { useReleaseNights } from './useReleaseNights';
import { useRevisionHolds } from './useRevisionHolds';
import { RemoveRevisionDialog } from './RemoveRevisionDialog';
import { useRevisionRemoval } from './useRevisionRemoval';
import { useRevisionReplay } from './useRevisionReplay';

interface Props {
  /**
   * Eingebettet (Plattform-Integrationen) verschwindet die Karte, solange sie lädt oder nichts
   * vorliegt — ein Ladefehler bleibt sichtbar.
   */
  embedded?: boolean;
}

/**
 * Buchungen, die der Kanalmanager geschickt hat und die es hier trotzdem nicht gibt.
 *
 * Ohne diese Fläche ist der Zustand von aussen nicht erkennbar: wir nehmen die Revision an,
 * quittieren sie beim Vendor (ein Retry brächte dasselbe Ergebnis) und legen bewusst keine Buchung
 * an. Beim Kanalmanager steht dann ein Haken, im Admin steht nichts — und der Betreiber sucht am
 * Webhook, obwohl die Begründung die ganze Zeit gespeichert war. Deshalb ist auch ein Ladefehler
 * oder fehlende Verbindung nie „alle übernommen", eingebettet ebenso wenig.
 *
 * Die Freigabe der Nächte öffnet beim Kanalmanager, was eine echte Buchung tragen könnte — sie
 * bietet die Fläche nur der Workspace-Verwaltung an; der Server prüft dasselbe.
 */
export function QuarantinedRevisionsCard({ embedded = false }: Props) {
  const { t } = useTranslation('revisionHolds');
  const opener = useRef<HTMLElement | null>(null);
  const card = useRef<HTMLDivElement | null>(null);
  const returnFocus = () => (opener.current?.isConnected ? opener.current : card.current)?.focus();
  const holds = useRevisionHolds();
  const problem = useApiProblem(holds.error);
  const replay = useRevisionReplay();
  const release = useReleaseNights();
  const removal = useRevisionRemoval();
  const canReleaseNights = isWorkspaceAdmin(useAuth().user);
  const list = holds.data;
  const dialogs = (
    <>
      {removal.target && (
        <RemoveRevisionDialog
          pending={removal.pending}
          error={removal.error}
          onClose={removal.close}
          onConfirm={removal.confirm}
          onReturnFocus={returnFocus}
        />
      )}
      {release.target && (
        <ReleaseNightsDialog
          preview={release.preview}
          loading={release.loading}
          ready={release.ready}
          error={release.error}
          pending={release.pending}
          onRetry={release.retry}
          onClose={release.close}
          onConfirm={() => release.confirm(release.target!)}
          onReturnFocus={returnFocus}
        />
      )}
    </>
  );

  if (!list) {
    if (holds.isError || holds.fetchStatus === 'paused') {
      return (
        <RevisionHoldsUnavailable
          offline={!holds.isError}
          reason={problem?.detail}
          onRetry={() => void holds.refetch()}
          className={embedded ? 'mt-6' : undefined}
        />
      );
    }
    return embedded ? null : <p className="text-ink-soft text-sm">Wird geladen …</p>;
  }
  const rows = withSettledRows(list.items, replay.settled);
  if (rows.length === 0) {
    return (
      <>
        {dialogs}
        {embedded ? null : (
          <Card ref={card} tabIndex={-1}>
            <EmptyState icon="Inbox" title={t('empty')} description={t('emptyDescription')} />
          </Card>
        )}
      </>
    );
  }

  return (
    <Card ref={card} tabIndex={-1} className={embedded ? 'mt-6' : undefined}>
      <CardBody className="p-5 sm:p-6">
        <RevisionHoldsIntro list={list} embedded={embedded} />
        {holds.error && (
          <p className="text-ink-soft mt-2 text-sm">
            Neu laden ist fehlgeschlagen — zu sehen ist der zuletzt geladene Stand.
          </p>
        )}
        <ul className="border-line divide-line mt-4 divide-y overflow-hidden rounded-lg border">
          {rows.map((row, index) => (
            <RevisionHoldRow
              key={row.id}
              row={row}
              automaticRetry={list.automaticRetry}
              replayState={replay.stateOf(row.id)}
              onReplay={() => replay.replay(row, index)}
              onRemove={
                canReleaseNights
                  ? (trigger) => {
                      opener.current = trigger;
                      removal.open(row);
                    }
                  : undefined
              }
              onReleaseNights={
                canReleaseNights
                  ? (trigger) => {
                      opener.current = trigger;
                      release.open(row.id);
                    }
                  : undefined
              }
            />
          ))}
        </ul>
        <MoreHolds list={list} />
      </CardBody>

      {dialogs}
    </Card>
  );
}
